import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Pencil, Plus, X } from "lucide-react";

export default function Zones() {
  const { zones, setZones, labels, sites, selectedSite } = useApp();
  const [editing, setEditing] = useState(null); // zone being edited
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => { api.get("/zones").then((r) => setZones(r.data)); }, [setZones]);

  const list = selectedSite === "all" ? zones : zones.filter((z) => z.siteId === selectedSite);
  const bySite = sites.map((s) => ({ site: s, zs: list.filter((z) => z.siteId === s.id) })).filter((g) => g.zs.length || selectedSite === s?.id);

  const startEdit = (z) => { setEditing(z.id); setDraft({ ...z, caracteristique: z.caracteristique || "", attributs: [...z.attributs], adj: [...z.adj] }); setAdding(false); };
  const startAdd = () => { setAdding(true); setEditing(null); setDraft({ siteId: selectedSite !== "all" ? selectedSite : sites[0]?.id, nom: "", caracteristique: "", attributs: [], adj: [] }); };
  const toggle = (k, v) => setDraft((d) => ({ ...d, [k]: d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v] }));

  const save = async () => {
    try {
      if (adding) { const { data } = await api.post("/zones", draft); setZones((z) => [...z, data]); toast.success("Zone créée"); }
      else { const { data } = await api.put(`/zones/${editing}`, draft); setZones((z) => z.map((x) => (x.id === editing ? data : x))); toast.success("Zone mise à jour"); }
      setEditing(null); setAdding(false); setDraft(null);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const Editor = () => (
    <div className="card" style={{ borderLeft: "4px solid var(--sarcelle)" }} data-testid="zone-editor">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ fontSize: "1rem" }}>{adding ? "Nouvelle zone" : `Édition · ${draft.nom}`}</h3>
        <button className="iconbtn" onClick={() => { setEditing(null); setAdding(false); setDraft(null); }}><X size={15} /></button>
      </div>
      {adding && <>
        <label className="f">Site</label>
        <select value={draft.siteId} onChange={(e) => setDraft({ ...draft, siteId: e.target.value })}>{sites.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}</select>
        <label className="f">Nom</label>
        <input value={draft.nom} onChange={(e) => setDraft({ ...draft, nom: e.target.value })} data-testid="zone-nom" />
      </>}
      <label className="f">Attributs de risque permanents</label>
      <div className="chips">
        {Object.entries(labels.attrs).map(([k, v]) => (
          <button type="button" key={k} className={`chip ${draft.attributs.includes(k) ? "on" : ""}`} onClick={() => toggle("attributs", k)} data-testid={`zone-attr-${k}`}>{v}</button>
        ))}
      </div>
      <label className="f">Caractéristique (référentiel spatial du plan de prévention)</label>
      <input value={draft.caracteristique || ""} onChange={(e) => setDraft({ ...draft, caracteristique: e.target.value })} data-testid="zone-caracteristique" placeholder="Ex : Travail en hauteur, garde-corps périmétriques — milieu occupé" />
      <label className="f">Zones adjacentes</label>
      <div className="chips">
        {zones.filter((z) => z.siteId === draft.siteId && z.id !== draft.id).map((z) => (
          <button type="button" key={z.id} className={`chip ${draft.adj.includes(z.id) ? "on" : ""}`} onClick={() => toggle("adj", z.id)}>{z.nom}</button>
        ))}
      </div>
      <div style={{ marginTop: 14 }}><button className="btn btn-p" onClick={save} disabled={!draft.nom} data-testid="zone-save">Enregistrer</button></div>
    </div>
  );

  return (
    <div>
      <div className="page-head">
        <div><div className="crumb">Référentiel</div><h1>Zones du site</h1></div>
        <button className="btn btn-p" onClick={startAdd} data-testid="add-zone-btn"><Plus size={15} />Ajouter une zone</button>
      </div>

      {(adding) && <Editor />}
      <div className="notice">Les attributs de zone alimentent directement le moteur : ajouter « Produits inflammables » rend le permis de feu bloquant pour tout point chaud déclaré dans la zone.</div>

      {bySite.map(({ site, zs }) => zs.length > 0 && (
        <div key={site.id}>
          <h2 style={{ fontSize: "1rem", margin: "16px 0 8px", color: "var(--text-muted)" }}>{site.nom}</h2>
          <div className="grid3">
            {zs.map((z) => editing === z.id ? <Editor key={z.id} /> : (
              <div className="card" key={z.id} data-testid={`zone-card-${z.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                  <h3 style={{ fontSize: "1rem" }}>{z.nom} <span className="mono" style={{ fontSize: ".62rem", color: "var(--text-muted)" }}>{z.id}</span></h3>
                  <button className="iconbtn" onClick={() => startEdit(z)} data-testid={`edit-zone-${z.id}`}><Pencil size={13} /></button>
                </div>
                <div style={{ margin: "8px 0" }}>{z.attributs.length ? z.attributs.map((a) => <span className="tag" key={a}>{labels.attrs[a]}</span>) : <span className="empty" style={{ padding: 0 }}>Aucun risque permanent</span>}</div>
                {z.caracteristique && <div style={{ fontSize: ".78rem", color: "var(--text-muted)", marginBottom: 4 }} data-testid={`zone-carac-${z.id}`}>{z.caracteristique}</div>}
                <div style={{ fontSize: ".76rem", color: "var(--text-muted)" }}>Adjacentes : {z.adj.map((id) => zones.find((x) => x.id === id)?.nom).filter(Boolean).join(", ") || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
